import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  TAX_SUMMARY_MONTH_KEYS,
  TAX_SUMMARY_MONTH_LABELS,
  formatTaxSummaryAmount,
} from "@/lib/taxSummaryWorkbookConstants";

const MONTH_FILTERS = [
  { value: "all", label: "Año completo" },
  ...TAX_SUMMARY_MONTH_KEYS.filter((k) => k !== "acumulado").map((k) => ({
    value: k,
    label: TAX_SUMMARY_MONTH_LABELS[k] || k,
  })),
];

/**
 * Informe por RFC: retenciones a favor (ingresos) o x pagar (gastos).
 * Solo lectura; no modifica el workbook.
 */
export default function TaxSummaryRetencionesInformePanel({
  fiscalClientId,
  year,
  side = "a_favor",
}) {
  const [monthKey, setMonthKey] = useState("all");
  const [loading, setLoading] = useState(false);
  const [informe, setInforme] = useState(null);

  const load = useCallback(async () => {
    if (!fiscalClientId || !year) return;
    setLoading(true);
    try {
      const data = await despachoFiscalInfoApi.getTaxSummaryRetencionesInforme({
        fiscalClientId,
        year,
        side,
        monthKey,
      });
      setInforme(data?.informe || null);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
      setInforme(null);
    } finally {
      setLoading(false);
    }
  }, [fiscalClientId, year, side, monthKey]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = informe?.summary_by_rfc || [];
  const items = informe?.items || [];

  return (
    <div
      className="space-y-3 rounded-sm border border-slate-200 bg-white p-3"
      data-testid={`tax-summary-retenciones-informe-${side}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-[#0F2D5C] m-0">
            {informe?.title || (side === "x_pagar" ? "Retenciones x pagar" : "Retenciones a favor")}
          </h2>
          <p className="text-[11px] text-slate-500 m-0 mt-0.5 max-w-3xl">
            {informe?.hint
              || "Generado desde CFDI vigentes. No altera el resumen mensual ni otros módulos."}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] text-slate-500">Periodo</Label>
            <Select value={monthKey} onValueChange={setMonthKey}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`retenciones-informe-month-${side}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_FILTERS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={loading}
            onClick={load}
            data-testid={`retenciones-informe-reload-${side}`}
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
            )}
            Actualizar
          </Button>
        </div>
      </div>

      {loading && !informe ? (
        <p className="text-xs text-slate-500 m-0 flex items-center gap-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando informe…
        </p>
      ) : null}

      {informe ? (
        <p className="text-[11px] text-slate-600 m-0">
          {informe.count_cfdi || 0} renglón(es) CFDI · {informe.count_rfc || 0} RFC ·
          Total retenido:{" "}
          <span className="font-semibold tabular-nums">
            {formatTaxSummaryAmount(String(informe.sum_amount || 0)) || "0.00"}
          </span>
        </p>
      ) : null}

      <div>
        <h3 className="text-xs font-semibold text-slate-800 m-0 mb-1.5">
          Resumen por RFC / tipo de retención
        </h3>
        {summary.length === 0 ? (
          <p className="text-[11px] text-slate-500 m-0">Sin retenciones en el periodo.</p>
        ) : (
          <div className="max-h-56 overflow-auto rounded-sm border border-slate-100">
            <table className="w-full text-[10px] border-collapse">
              <thead className="sticky top-0 bg-slate-50 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1 border-b">RFC</th>
                  <th className="text-left px-2 py-1 border-b">Nombre</th>
                  <th className="text-left px-2 py-1 border-b">Tipo</th>
                  <th className="text-right px-2 py-1 border-b">Acumulado</th>
                  <th className="text-right px-2 py-1 border-b">CFDI</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr
                    key={`${row.rfc}-${row.tipo_retencion}`}
                    className="border-b border-slate-50"
                    data-testid={`retenciones-summary-${side}-${row.rfc}-${row.tipo_retencion}`}
                  >
                    <td className="px-2 py-1 font-mono">{row.rfc || "—"}</td>
                    <td className="px-2 py-1 truncate max-w-[14rem]">{row.nombre || "—"}</td>
                    <td className="px-2 py-1">{row.tipo_retencion_label || row.tipo_retencion}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-medium">
                      {formatTaxSummaryAmount(String(row.acumulado || 0)) || "—"}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{row.count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-xs font-semibold text-slate-800 m-0 mb-1.5">
          CFDI considerados
        </h3>
        {items.length === 0 ? (
          <p className="text-[11px] text-slate-500 m-0">Sin CFDI en el periodo.</p>
        ) : (
          <div className="max-h-64 overflow-auto rounded-sm border border-slate-100">
            <table className="w-full text-[10px] border-collapse">
              <thead className="sticky top-0 bg-slate-50 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1 border-b">Fecha</th>
                  <th className="text-left px-2 py-1 border-b">RFC</th>
                  <th className="text-left px-2 py-1 border-b">Nombre</th>
                  <th className="text-left px-2 py-1 border-b">Tipo</th>
                  <th className="text-left px-2 py-1 border-b">Origen</th>
                  <th className="text-right px-2 py-1 border-b">Retenido</th>
                  <th className="text-left px-2 py-1 border-b">UUID</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr
                    key={`${it.uuid}-${it.tipo_retencion}-${idx}`}
                    className="border-b border-slate-50"
                  >
                    <td className="px-2 py-1 whitespace-nowrap">{it.fecha || "—"}</td>
                    <td className="px-2 py-1 font-mono">{it.rfc || "—"}</td>
                    <td className="px-2 py-1 truncate max-w-[10rem]">{it.nombre || "—"}</td>
                    <td className="px-2 py-1">{it.tipo_retencion_label || it.tipo_retencion}</td>
                    <td className="px-2 py-1">{it.metodo_pago || "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {formatTaxSummaryAmount(String(it.amount || 0)) || "—"}
                    </td>
                    <td className="px-2 py-1 font-mono text-[9px] truncate max-w-[8rem]" title={it.uuid}>
                      {it.uuid || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
